import { NgClass, NgStyle } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    inject,
    type OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ListboxModule } from '@openng/optimus-ui/listbox';
import { PopoverModule } from '@openng/optimus-ui/popover';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import DataSource from 'devextreme/data/data_source';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { catchError, lastValueFrom, map, of } from 'rxjs';

import { JobRuntimeInfoComponent } from '@app/shared/components/run-job/run-job-details/job-runtime-info/job-runtime-info.component';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton.component';
import { StatusIconComponent } from '@app/shared/components/status-icon/status-icon.component';
import { SCHEDULED_JOB_STATUS, SKELETON_TYPES } from '@app/shared/constants/shared.constant';
import { CorDxGridSearchDirective } from '@app/shared/directives/dx-grid-search.directive';
import type { DxDataGrid } from '@app/shared/interface/devextreme.interface';
import { JobModel } from '@app/shared/models/job.model';
import type { JobBoardAdapter } from '@app/shared/models/job-board.model';
import { LocalDatePipe } from '@app/shared/pipes/date-format.pipe';
import { NameBadgePipe } from '@app/shared/pipes/name-badge.pipe';
import { TimeTakenPipe } from '@app/shared/pipes/time-taken.pipe';
import { UniqueRgbColorPipe } from '@app/shared/pipes/unique-rgb-color.pipe';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { ClipboardService } from '@app/shared/services/clipboard.service';
import { JobBoardService } from '@app/shared/services/job-board.service';
import { DataGridHelpers } from '@app/shared/utils/data-grid-helpers';
import { Helpers } from '@app/shared/utils/helpers';
import { RouterHelper } from '@app/shared/utils/router-helper';

@Component({
    selector: 'cor-job-board',
    templateUrl: './job-board.component.html',
    styleUrls: ['./job-board.component.scss'],
    imports: [
        ButtonModule,
        DxDataGridModule,
        TooltipModule,
        StatusIconComponent,
        JobRuntimeInfoComponent,
        RouterModule,
        LocalDatePipe,
        SkeletonComponent,
        TimeTakenPipe,
        NgClass,
        NgStyle,
        AvatarModule,
        NameBadgePipe,
        UniqueRgbColorPipe,
        CorDxGridSearchDirective,
        PopoverModule,
        ListboxModule,
    ],
})
export class JobBoardComponent implements OnInit {
    readonly dataGrid = viewChild(DxDataGridComponent);

    protected jobDataSource: DataSource<JobBoardAdapter & { url?: string }>;

    protected readonly pageSize = computed(
        () => this.appConfigService.appConfig().paginationItemsPerPage,
    );

    protected readonly isJobDataLoading = signal(true);

    readonly service = inject(JobBoardService);
    private readonly appConfigService = inject(AppConfigService);
    protected readonly clipboardService = inject(ClipboardService);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);

    protected SCHEDULED_JOB_STATUS = SCHEDULED_JOB_STATUS;
    protected readonly SKELETON_TYPES = SKELETON_TYPES;
    protected readonly DataGridHelpers = DataGridHelpers;
    protected readonly cards = this.service.cards.asReadonly();

    ngOnInit(): void {
        // In case jobInterval is not provided, fetch the job list here again to load the page
        this.service.fetchJobBoardData();

        this.service.jobBoardRefreshing$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.getJobBoardData();
        });
    }

    /**
     * Start/Stop job polling based runtime info dialog
     * visibility state.
     * @param visible runtime info pop up visibility state
     */
    protected onShowHideRuntimeInfo(visible: boolean) {
        this.service.toggleJobPolling(visible);
    }

    private getJobBoardData(): void {
        this.jobDataSource = new DataSource({
            load: () => {
                return lastValueFrom(
                    this.service.getJobBoardData().pipe(
                        map(({ result }) => {
                            this.isJobDataLoading.set(false);

                            return {
                                data: (result || []).map(item => ({
                                    ...item,
                                    url: item.hasAccess
                                        ? RouterHelper.buildRoute(item.linkInfo)
                                        : undefined,
                                    jobSteps: (item.jobSteps || []).map(step => ({
                                        ...step,
                                        label: JobModel.getStepLabel(
                                            step,
                                            item.objectName,
                                            item.queuePosition,
                                        ),
                                    })),
                                })),
                            };
                        }),
                        catchError(err => {
                            console.log(Helpers.getErrorMessageString(err));
                            this.isJobDataLoading.set(false);

                            // Catch the error and return empty data so we don't show [object Object]
                            // in the UI in case of errors.
                            return of({ data: [] });
                        }),
                    ),
                );
            },
        });
    }

    protected onRowClick(data: DxDataGrid.RowClickEvent<JobBoardAdapter & { url?: string }>) {
        if (data.data?.hasAccess) {
            this.router.navigateByUrl(data.data.url);
        }
    }

    protected onContextMenuPreparing(e: DxDataGrid.ContextMenuPreparingEvent): void {
        if (e.row?.rowType === 'data') {
            e.items = [
                ...(e.items || []),
                {
                    text: 'Open In New Tab',
                    onItemClick: () => window.open(e.row.data.url, '_blank'),
                },
                {
                    text: 'Copy Link',
                    onItemClick: () =>
                        this.clipboardService.copyToClipboard(
                            window.location.origin + e.row.data.url,
                        ),
                },
            ];
        } else {
            e.items = [
                ...(e.items || []),
                {
                    text: 'Reset to original state',
                    onItemClick: () => e.component.state(null),
                },
            ];
        }
    }
}
